import React from "react";
import {
    Alert,
    Autocomplete,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    TextField,
    Tooltip,
} from "@mui/material";
import {AddOutlined, DeleteOutlined} from "@mui/icons-material";
import type {ContractDetailApi} from "../useContractDetailQuery.ts";
import type {ReviewItemDraft, ReviewTermDraft} from "../useReviewContractMutation.ts";
import {isVersionConflict, useReviewContractMutation} from "../useReviewContractMutation.ts";

/** Header fields a review can change. The lifecycle is not among them: it is
 *  derived from whether the contract is signed plus these dates, so signing it
 *  (or the dates moving) is what advances it. */
const HEADER_FIELDS = [
    {key: 'contract_title', label: 'Title', type: 'text'},
    {key: 'supplier_name', label: 'Supplier', type: 'text'},
    {key: 'contract_status', label: 'Signing', type: 'signing'},
    {key: 'effective_date', label: 'Effective', type: 'date'},
    {key: 'expiration_date', label: 'Expires', type: 'date'},
    {key: 'contract_type', label: 'Contract type', type: 'type'},
] as const;

type HeaderKey = typeof HEADER_FIELDS[number]['key'];
type HeaderDraft = Record<HeaderKey, string>;

const ITEM_COLUMNS: {key: keyof ReviewItemDraft; label: string; type: 'text' | 'number'; width?: number}[] = [
    {key: 'item_name', label: 'Item', type: 'text'},
    {key: 'item_quantity', label: 'Qty', type: 'number', width: 70},
    {key: 'item_unit', label: 'Unit', type: 'text', width: 90},
    {key: 'item_unit_price', label: 'Unit price', type: 'number', width: 100},
    {key: 'item_spend_net', label: 'Net', type: 'number', width: 100},
    {key: 'item_spend_gross', label: 'Gross', type: 'number', width: 100},
    {key: 'item_price_currency', label: 'Currency', type: 'text', width: 90},
    {key: 'payment_frequency', label: 'Billing', type: 'text', width: 110},
];

const TERM_COLUMNS: {key: keyof ReviewTermDraft; label: string}[] = [
    {key: 'term_type', label: 'Type'},
    {key: 'net_days_value', label: 'Net days'},
    {key: 'payment_method', label: 'Method'},
    {key: 'term_details', label: 'Details'},
];

const toHeaderDraft = (data: ContractDetailApi): HeaderDraft => ({
    contract_title: data.contract_title ?? '',
    supplier_name: data.supplier_name ?? '',
    effective_date: data.effective_date?.slice(0, 10) ?? '',
    expiration_date: data.expiration_date?.slice(0, 10) ?? '',
    contract_type: data.contract_type ?? '',
    // The raw parsed value can be anything ('active', 'terminated', …); the
    // select offers the two states it cleans into, so show the cleaned one.
    contract_status: data.lifecycle === 'draft' ? 'draft' : 'signed',
});

const toItemDrafts = (data: ContractDetailApi): ReviewItemDraft[] => data.items.map(item => ({
    item_name: item.item_name ?? '',
    item_description: item.item_description ?? '',
    item_quantity: item.item_quantity,
    item_unit: item.item_unit ?? '',
    item_unit_price: item.item_unit_price,
    item_price_currency: item.item_price_currency ?? '',
    item_spend_net: item.item_spend_net,
    item_spend_gross: item.item_spend_gross,
    payment_frequency: item.payment_frequency ?? '',
    pricing_model: item.pricing_model ?? '',
}));

const toTermDrafts = (data: ContractDetailApi): ReviewTermDraft[] => data.payment_terms.map(term => ({
    term_type: term.term_type ?? '',
    net_days_value: term.net_days_value ?? '',
    payment_method: term.payment_method ?? '',
    term_details: term.term_details ?? '',
}));

const EMPTY_ITEM: ReviewItemDraft = {
    item_name: '', item_description: '', item_quantity: null, item_unit: '',
    item_unit_price: null, item_price_currency: '', item_spend_net: null,
    item_spend_gross: null, payment_frequency: '', pricing_model: '',
};

const EMPTY_TERM: ReviewTermDraft = {term_type: '', net_days_value: '', payment_method: '', term_details: ''};

type Props = {
    bagId: number;
    data: ContractDetailApi;
    /** Canonical contract-type labels to suggest, most relevant first. */
    typeSuggestions: string[];
    /** Leaves edit mode; `saved` is false for cancel and for a no-op save. */
    onDone: (saved: boolean) => void;
};

/**
 * The review form: edits become one new user version on save (which freezes
 * further parses for this contract). Items and payment terms are saved as
 * whole sets — that is how adding and removing rows is expressed.
 */
export const ContractDetailEdit: React.FC<Props> = ({bagId, data, typeSuggestions, onDone}) => {
    const [header, setHeader] = React.useState<HeaderDraft>(() => toHeaderDraft(data));
    const [items, setItems] = React.useState<ReviewItemDraft[]>(() => toItemDrafts(data));
    const [terms, setTerms] = React.useState<ReviewTermDraft[]>(() => toTermDrafts(data));
    const {mutate, isPending, error} = useReviewContractMutation();
    const loadedType = React.useMemo(() => toHeaderDraft(data).contract_type, [data]);

    const setItem = (index: number, patch: Partial<ReviewItemDraft>) =>
        setItems(previous => previous.map((row, i) => i === index ? {...row, ...patch} : row));
    const setTerm = (index: number, patch: Partial<ReviewTermDraft>) =>
        setTerms(previous => previous.map((row, i) => i === index ? {...row, ...patch} : row));

    const save = () => {
        // Only what actually changed goes up: untouched header fields and whole
        // untouched sections copy forward server-side, which keeps the
        // changed-columns note honest and spares a no-op edit a round trip.
        const original = toHeaderDraft(data);
        const changedHeader = Object.fromEntries(
            (Object.keys(header) as HeaderKey[])
                .filter(key => header[key] !== original[key])
                .map(key => [key, header[key] || null]),
        );
        const nonEmpty = (rows: object[]) =>
            rows.filter(row => Object.values(row).some(value => value !== null && value !== ''));
        const itemsDraft = nonEmpty(items) as ReviewItemDraft[];
        const termsDraft = nonEmpty(terms) as ReviewTermDraft[];
        // Same builders produced both sides, so key order matches and a text
        // comparison is a faithful deep comparison here.
        const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
        const itemsChanged = !same(itemsDraft, nonEmpty(toItemDrafts(data)));
        const termsChanged = !same(termsDraft, nonEmpty(toTermDrafts(data)));

        // Saving without edits is not a new version — just leave edit mode.
        if (Object.keys(changedHeader).length === 0 && !itemsChanged && !termsChanged) {
            onDone(false);
            return;
        }

        mutate({
            bagId,
            contractId: data.id,
            expectedVersionNo: data.version_no,
            header: changedHeader,
            ...itemsChanged && {items: itemsDraft},
            ...termsChanged && {paymentTerms: termsDraft},
        }, {onSuccess: () => onDone(true)});
    };

    const numberValue = (raw: string): number | null => raw === '' ? null : Number(raw);

    return (
        <div className="cd-edit">
            {error && (
                <Alert severity={isVersionConflict(error) ? 'warning' : 'error'} className="cd-edit-alert">
                    {isVersionConflict(error)
                        ? 'This contract changed since you opened it. Close and reopen it, then re-apply your edits.'
                        : `Could not save the review: ${error.message}`}
                </Alert>
            )}

            <div className="cd-summary">
                <dl className="cd-facts">
                    {HEADER_FIELDS.map(field => (
                        <div key={field.key}>
                            <dt>{field.label}</dt>
                            <dd>
                                {field.type === 'type'
                                    // Suggest the vocabulary, never force it:
                                    // the backend keeps an unmapped type
                                    // verbatim and flags it, so free text is a
                                    // valid answer.
                                    ? (
                                        <Autocomplete
                                            freeSolo
                                            openOnFocus
                                            selectOnFocus
                                            size="small"
                                            options={typeSuggestions}
                                            inputValue={header[field.key]}
                                            onInputChange={(_, value) => setHeader(previous => ({
                                                ...previous, [field.key]: value,
                                            }))}
                                            filterOptions={(options, state) => {
                                                const typed = state.inputValue.trim();
                                                // Opening the field on the value
                                                // it loaded with should show the
                                                // whole vocabulary; filter only
                                                // once the reviewer types.
                                                if (!typed || typed === loadedType) return options;
                                                const needle = typed.toLowerCase();
                                                return options.filter(o => o.toLowerCase().includes(needle));
                                            }}
                                            renderInput={params => <TextField {...params} fullWidth/>}
                                        />
                                    )
                                    : field.type === 'signing'
                                    // Signedness is binary, so it is a tick: on
                                    // = signed, off = draft. The backend cleans
                                    // these two values, and the lifecycle
                                    // recomputes from them on save.
                                        ? (
                                            <FormControlLabel
                                            className="cd-edit-tick"
                                            control={
                                                <Checkbox
                                                    size="small"
                                                    checked={header[field.key] === 'signed'}
                                                    onChange={e => setHeader(previous => ({
                                                        ...previous,
                                                        [field.key]: e.target.checked ? 'signed' : 'draft',
                                                    }))}
                                                />
                                            }
                                            label={header[field.key] === 'signed' ? 'Signed' : 'Draft'}
                                            />
                                        )
                                        : (
                                            <TextField
                                            size="small"
                                            fullWidth
                                            type={field.type}
                                            value={header[field.key]}
                                            onChange={e => setHeader(previous => ({
                                                ...previous, [field.key]: e.target.value,
                                            }))}
                                            />
                                        )}
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>

            <h2 className="cd-section">Items ({items.length})</h2>
            <div className="cd-table-scroll">
                <table className="cd-table cd-edit-table">
                    <thead>
                        <tr>
                            {ITEM_COLUMNS.map(c => <th key={c.key}>{c.label}</th>)}
                            <th aria-label="Remove"/>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((row, index) => (
                            // Rows are a replacement set with no identity of their
                            // own, so the index is the only stable key there is.
                            <tr key={index}>
                                {ITEM_COLUMNS.map(column => (
                                    <td key={column.key} style={column.width ? {width: column.width} : undefined}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            type={column.type}
                                            value={row[column.key] ?? ''}
                                            onChange={e => setItem(index, {
                                                [column.key]: column.type === 'number'
                                                    ? numberValue(e.target.value)
                                                    : e.target.value,
                                            })}
                                        />
                                    </td>
                                ))}
                                <td className="cd-edit-remove">
                                    <Tooltip title="Remove item">
                                        <IconButton
                                            size="small"
                                            onClick={() => setItems(previous => previous.filter((_, i) => i !== index))}>
                                            <DeleteOutlined fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Button size="small" startIcon={<AddOutlined/>}
                    onClick={() => setItems(previous => [...previous, EMPTY_ITEM])}>
                Add item
            </Button>

            <h2 className="cd-section">Payment terms ({terms.length})</h2>
            <div className="cd-table-scroll">
                <table className="cd-table cd-edit-table">
                    <thead>
                        <tr>
                            {TERM_COLUMNS.map(c => <th key={c.key}>{c.label}</th>)}
                            <th aria-label="Remove"/>
                        </tr>
                    </thead>
                    <tbody>
                        {terms.map((row, index) => (
                            <tr key={index}>
                                {TERM_COLUMNS.map(column => (
                                    <td key={column.key}>
                                        <TextField
                                            size="small"
                                            fullWidth
                                            value={row[column.key]}
                                            onChange={e => setTerm(index, {[column.key]: e.target.value})}
                                        />
                                    </td>
                                ))}
                                <td className="cd-edit-remove">
                                    <Tooltip title="Remove term">
                                        <IconButton
                                            size="small"
                                            onClick={() => setTerms(previous => previous.filter((_, i) => i !== index))}>
                                            <DeleteOutlined fontSize="small"/>
                                        </IconButton>
                                    </Tooltip>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Button size="small" startIcon={<AddOutlined/>}
                    onClick={() => setTerms(previous => [...previous, EMPTY_TERM])}>
                Add payment term
            </Button>

            <div className="cd-edit-actions">
                <Button variant="outlined" onClick={() => onDone(false)} disabled={isPending}>Cancel</Button>
                <Button variant="contained" onClick={save} disabled={isPending}>
                    {isPending ? 'Saving…' : 'Save review'}
                </Button>
            </div>
        </div>
    );
};
